import get from 'lodash.get';
import PropTypes from 'prop-types';
import React from 'react';
import { Client, HydrationProvider } from 'react-hydration-provider';
import { useInView } from 'react-intersection-observer';
import ReactPlayer from 'react-player/lazy';

import { getThemeClass } from '../../tools/textTools';
import ContentfulImage from '../Common/ContentfulImage';
import { ArrowLink } from '../Common/Links';
import RichTextConverter from '../Common/RichText/RichTextConverter';
import SimpleCard from '../Common/SimpleCard';
import PageSection from './PageSection';
import * as styles from './ThreeUp.module.scss';

const ThreeUp = ({ section, id, center = false, className = '', animate = false, wide = false }) => {
  const { ref: containerRef, inView: isVisible } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const themeClass = getThemeClass(section.colorTheme?.themeName);

  const cardMaxWidth = section.itemsPerRow === 2 ? { maxWidth: 'unset' } : {};
  const isSvg = section.mainImage && section.mainImage.file?.contentType === 'image/svg+xml';
  let heightStyle = styles.free;

  if (section.adjustForTallImage && !isSvg) {
    heightStyle = styles.heightAdjust;
  }
  let mainMedia;
  if (section.mainImage) {
    mainMedia = <ContentfulImage imageFragment={section.mainImage} className={[heightStyle, styles.svg].join(' ')} />;
  }
  if (section.wistiaUrl) {
    mainMedia = (
      <div className={`player-wrapper-${section.wistiaUrl}`}>
        <HydrationProvider>
          <Client>
            <ReactPlayer
              className={`react-player-${section.wistiaUrl}`}
              controls={false}
              width={'100%'}
              height="100%"
              url={section.wistiaUrl}
            />
          </Client>
        </HydrationProvider>
      </div>
    );
  }

  const cards = section.media || section.content || [];
  const cardsAreCentered = cards.some((c) => c.textJustification === 'Center');
  let itemsPerRow = 3;
  if (section.itemsPerRow) itemsPerRow = section.itemsPerRow;
  else if (cards.length % 4 === 0 || cards.length === 2) itemsPerRow = 2;

  const backgroundColors = {
    White: styles.white,
    Emerald: styles.emerald,
    'Emerald Light': styles.emeraldLight,
    Evergreen: styles.evergreen,
    'Evergreen Deep': styles.evergreenDeep,
  };

  return (
    <PageSection
      slim
      wide={wide}
      className={[
        styles.section,
        backgroundColors[section.backgroundColor],
        themeClass,
        className,
        animate && styles.animated,
      ].join(' ')}
      data-analytics-section={'three-up'}
    >
      <div className={[wide ? styles.container : '', center || cardsAreCentered ? styles.center : ''].join(' ')}>
        <div className={styles.flex} data-analytics-section={'three-up-heading'}>
          <div className={styles.gridLeft}>
            {section.eyebrow && <div className={[styles.eyebrow, 'themed-eyebrow'].join(' ')}>{section.eyebrow}</div>}
            {section.heading ? (
              section.headingSize === 'H2' ? (
                <h2 id={id || ''}>{section.heading}</h2>
              ) : (
                <h3 id={id || ''}>{section.heading}</h3>
              )
            ) : (
              section.title && <h2 id={id || ''}>{section.title}</h2>
            )}
            {section.sectionCopy && <RichTextConverter className={styles.shortText} copy={section.sectionCopy} />}
          </div>
          <div className={styles.gridRight}>{mainMedia}</div>
        </div>
        <div
          ref={containerRef}
          className={[styles.grid, isVisible && animate && styles.fade].join(' ')}
          style={{ '--itemsPerRow': itemsPerRow }}
        >
          {(cards || [])
            //.filter((c) => c.__typename === 'ContentfulComponentSimpleContent')
            .map((card, i) => {
              let cardImage = !!card.image?.icon ? card?.image?.icon?.src : card?.image?.file?.url;
              return (
                <SimpleCard
                  data-analytics-component={'three-up-card'}
                  data-analytics-id={`${card.title}-${i}`}
                  key={i}
                  style={{ '--index': i, ...cardMaxWidth }}
                  center={card.textJustification === 'Center' || section.centeredTitle}
                  imageSrc={cardImage}
                  body={card.body || card.copy}
                  title={card?.title || ''}
                  imageType={'icon'}
                  className={animate ? styles.hidden : null}
                  imageWidth={card.image?.width}
                  imageHeight={card.image?.height}
                />
              );
            })}
        </div>
      </div>
      {section?.ctaUrl && <ArrowLink label={section?.ctaLabel || ''} url={section?.ctaUrl || ''} />}
    </PageSection>
  );
};

export default ThreeUp;
